import React, { useState, useEffect } from 'react';
import {
    Home,
    TrendingUp,
    Users,
    FileText,
    Wrench,
    File,
    MessageCircle,
    User,
    Lock,
    CheckCircle,
    ExternalLink,
    Download,
    Plus,
    Settings,
    Star,
    Crown,
    Zap,
    Menu,
    X,
    DollarSign,
    Award,
    Calendar,
    Eye,
    Copy,
    Construction,
    ChevronRight,
    ChevronDown,
    ArrowUpCircle,
    Gift,
    Clock,
    TrendingDown,
    BarChart3,
    RefreshCw,
    AlertCircle,
    Loader2
} from 'lucide-react';

const API_BASE = 'https://api.b7774c.ru/api/v1';

const ConcordWebApp = () => {
    const [activeTab, setActiveTab] = useState('home');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [expandedPartners, setExpandedPartners] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // API States
    const [userData, setUserData] = useState(null);
    const [rewardsHistory, setRewardsHistory] = useState([]);
    const [partnersStructure, setPartnersStructure] = useState([]);
    const [marketingData, setMarketingData] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('concord_token') || null);

    // API Functions
    const apiCall = async (endpoint, options = {}) => {
        try {
            const response = await fetch(`${API_BASE}${endpoint}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : '',
                    ...options.headers
                },
                ...options
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'API Error');
            }

            return data;
        } catch (err) {
            console.error('API Error:', err);
            throw err;
        }
    };

    // Load initial data
    useEffect(() => {
        const initializeApp = async () => {
            if (!token) {
                // Simulate Telegram WebApp login
                try {
                    // In real app, get data from Telegram WebApp
                    const tgData = {
                        telegram_id: 123456789,
                        first_name: "Михаил",
                        last_name: "Петров",
                        username: "mikhail_p",
                        auth_date: Math.floor(Date.now() / 1000),
                        hash: "mock_hash"
                    };

                    const loginResponse = await apiCall('/auth/telegram', {
                        method: 'POST',
                        body: JSON.stringify(tgData)
                    });

                    if (loginResponse.success) {
                        setToken(loginResponse.data.token);
                        localStorage.setItem('concord_token', loginResponse.data.token);
                        setUserData(loginResponse.data.user);
                    }
                } catch (err) {
                    setError('Ошибка авторизации');
                    // Use demo data if API fails
                    setUserData({
                        id: 12345,
                        telegram_id: 123456789,
                        first_name: "Михаил",
                        last_name: "Петров",
                        full_name: "Михаил Петров",
                        balance: 2847.50,
                        user_level: 3,
                        active_referrals_count: 12,
                        created_at: "2024-01-15T00:00:00.000000Z"
                    });
                }
            }

            await loadDashboardData();
            setLoading(false);
        };

        initializeApp();
    }, []);

    const loadDashboardData = async () => {
        try {
            if (token) {
                // Load user profile
                const profileResponse = await apiCall('/user/profile');
                if (profileResponse.success) {
                    setUserData(profileResponse.data);
                }

                // Load rewards history
                const rewardsResponse = await apiCall('/operations/rewards');
                if (rewardsResponse.success) {
                    setRewardsHistory(rewardsResponse.data);
                }

                // Load partners structure
                const partnersResponse = await apiCall('/user/partners');
                if (partnersResponse.success) {
                    setPartnersStructure(partnersResponse.data);
                }

                // Load marketing data
                const marketingResponse = await apiCall('/marketing/');
                if (marketingResponse.success) {
                    setMarketingData(marketingResponse.data);
                }
            }
        } catch (err) {
            console.error('Failed to load dashboard data:', err);
            // Use demo data on API failure
            loadDemoData();
        }
    };

    const loadDemoData = () => {
        setRewardsHistory([
            {
                id: 1,
                amount: 50.00,
                comment: "Реферальное вознаграждение от партнера Алексей С.",
                created_at: "2024-12-20 15:30",
                status_name: "Выполнено",
                partner_info: { partner_name: "Алексей С.", level: 2 }
            },
            {
                id: 2,
                amount: 25.00,
                comment: "Бонус с 2 уровня от партнера Мария К.",
                created_at: "2024-12-19 12:15",
                status_name: "Выполнено",
                partner_info: { partner_name: "Мария К.", level: 3 }
            }
        ]);

        setPartnersStructure([
            {
                id: 101,
                name: "Алексей Смирнов",
                level: 2,
                total_earnings: 450.00,
                joined_at: "2024-11-20",
                active: true,
                children: [
                    {
                        id: 102,
                        name: "Елена Иванова",
                        level: 1,
                        total_earnings: 150.00,
                        joined_at: "2024-12-01",
                        active: true,
                        children: []
                    }
                ]
            }
        ]);
    };

    const copyReferralLink = () => {
        const link = `https://b7774c.ru/ref/${userData?.id}`;
        navigator.clipboard.writeText(link);
        // Show toast notification in real app
        alert('Ссылка скопирована!');
    };

    const exportRewards = async () => {
        try {
            const response = await apiCall('/operations/export', {
                method: 'POST',
                body: JSON.stringify({
                    format: 'csv',
                    date_from: '2024-01-01',
                    date_to: new Date().toISOString().split('T')[0]
                })
            });

            if (response.success) {
                // Handle export data
                console.log('Export data:', response.data);
                alert('Экспорт готов!');
            }
        } catch (err) {
            console.error('Export failed:', err);
            alert('Ошибка экспорта');
        }
    };

    const menuItems = [
        { id: 'home', label: 'Главная', icon: Home },
        { id: 'finances', label: 'Финансы', icon: DollarSign },
        { id: 'partners', label: 'Партнеры', icon: Users },
        { id: 'structure', label: 'Структура', icon: BarChart3 },
        { id: 'marketing', label: 'Маркетинг', icon: TrendingUp },
        { id: 'tools', label: 'Наши Инструменты', icon: Wrench },
        { id: 'presentations', label: 'Презентации', icon: FileText },
        { id: 'documents', label: 'Документы', icon: File },
        { id: 'groups', label: 'Группы и каналы', icon: MessageCircle }
    ];

    const tools = [
        {
            id: 'businessCard',
            name: 'Визитка',
            level: 1,
            price: 10,
            description: 'Персональная визитка для привлечения клиентов',
            status: 'В разработке',
            gradient: 'from-emerald-500 to-emerald-600'
        },
        {
            id: 'videoLanding',
            name: 'Видео-лендинг',
            level: 2,
            price: 15,
            description: 'Продающий лендинг с видео презентацией',
            status: 'В разработке',
            gradient: 'from-emerald-600 to-green-600'
        },
        {
            id: 'miniLanding',
            name: 'Мини-лендинг',
            level: 3,
            price: 15,
            description: 'Компактный лендинг для быстрой конверсии',
            status: 'В разработке',
            gradient: 'from-green-600 to-green-700'
        },
        {
            id: 'telegramBot',
            name: 'Телеграм-бот',
            level: 4,
            price: 20,
            description: 'Автоматизированный бот для работы с клиентами',
            status: 'В разработке',
            gradient: 'from-amber-500 to-yellow-600'
        },
        {
            id: 'aiCatalog',
            name: 'Каталог ИИ',
            level: 5,
            price: null,
            description: 'Полный каталог AI инструментов (PDF)',
            status: 'В разработке',
            gradient: 'from-yellow-600 to-amber-600'
        },
        {
            id: 'businessJournal',
            name: 'Бизнесжурнал',
            level: 6,
            price: null,
            description: 'Доступ к бизнес-журналу',
            status: 'В разработке',
            gradient: 'from-amber-600 to-orange-600'
        }
    ];

    const PartnerNode = ({ partner, depth = 0 }) => {
        const isExpanded = expandedPartners[partner.id];
        const hasChildren = partner.children && partner.children.length > 0;

        const toggleExpanded = () => {
            setExpandedPartners(prev => ({
                ...prev,
                [partner.id]: !prev[partner.id]
            }));
        };

        const getLevelColor = (level) => {
            const colors = {
                1: 'bg-gray-500/20 text-gray-300',
                2: 'bg-blue-500/20 text-blue-300',
                3: 'bg-green-500/20 text-green-300',
                4: 'bg-yellow-500/20 text-yellow-300',
                5: 'bg-orange-500/20 text-orange-300',
                6: 'bg-red-500/20 text-red-300'
            };
            return colors[level] || colors[1];
        };

        return (
            <div className={`${depth > 0 ? 'ml-6 border-l border-white/10 pl-4' : ''}`}>
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4 mb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {hasChildren && (
                                <button
                                    onClick={toggleExpanded}
                                    className="p-1 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                                >
                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </button>
                            )}

                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                                    <User className="text-white" size={20} />
                                </div>
                                <div>
                                    <div className="text-white font-medium">{partner.name}</div>
                                    <div className="text-gray-400 text-sm">
                                        Присоединился {new Date(partner.joined_at).toLocaleDateString('ru-RU')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className={`px-2 py-1 rounded-full text-xs font-semibold ${getLevelColor(partner.level)}`}>
                                Ур.{partner.level}
                            </div>
                            <div className="text-right">
                                <div className="text-emerald-400 font-semibold">${partner.total_earnings?.toFixed(2) || '0.00'}</div>
                                <div className="text-gray-400 text-xs">общий доход</div>
                            </div>
                        </div>
                    </div>
                </div>

                {hasChildren && isExpanded && (
                    <div className="space-y-2">
                        {partner.children.map(child => (
                            <PartnerNode key={child.id} partner={child} depth={depth + 1} />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const ToolCard = ({ tool }) => {
        const userLevel = userData?.user_level || 1;
        const hasAccess = userLevel >= tool.level;

        return (
            <div className="relative group">
                <div className={`relative overflow-hidden rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-6 transition-all duration-300 hover:scale-105 hover:bg-white/15 ${!hasAccess ? 'opacity-60' : ''}`}>
                    <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${tool.gradient}`}></div>

                    <div className="absolute top-4 right-4">
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${hasAccess ? 'bg-emerald-500/20 text-emerald-300' : 'bg-gray-500/20 text-gray-400'}`}>
                            <Crown size={12} />
                            Ур.{tool.level}
                        </div>
                    </div>

                    <div className="absolute top-4 left-4">
                        <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-500/20 text-orange-300">
                            <Construction size={12} />
                            {tool.status}
                        </div>
                    </div>

                    <div className="mt-8">
                        <h3 className="text-xl font-bold text-white mb-2">{tool.name}</h3>
                        <p className="text-gray-300 text-sm mb-4">{tool.description}</p>

                        {hasAccess ? (
                            <div className="space-y-3">
                                {tool.price && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-400">Стоимость:</span>
                                        <span className="text-white font-semibold">${tool.price}</span>
                                    </div>
                                )}

                                <button className="w-full bg-gray-500/20 text-gray-400 py-2 px-4 rounded-lg cursor-not-allowed flex items-center justify-center gap-2">
                                    <Construction size={16} />
                                    В разработке
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                                <Lock size={16} />
                                Требуется уровень {tool.level}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="flex items-center gap-3 text-white">
                        <Loader2 className="animate-spin" size={24} />
                        <span>Загрузка данных...</span>
                    </div>
                </div>
            );
        }

        if (error && !userData) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <AlertCircle className="text-red-400 mx-auto mb-4" size={48} />
                        <h3 className="text-white text-xl mb-2">Ошибка загрузки</h3>
                        <p className="text-gray-400 mb-4">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
                        >
                            <RefreshCw size={16} />
                            Попробовать снова
                        </button>
                    </div>
                </div>
            );
        }

        switch (activeTab) {
            case 'home':
                return (
                    <div className="space-y-8">
                        <div className="text-center">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/20 to-green-500/20 border border-emerald-500/30 mb-6">
                                <Zap className="text-emerald-400" size={20} />
                                <span className="text-emerald-300 font-semibold">БЕСКОНЕЧНЫЙ ДОХОД</span>
                            </div>
                            <h1 className="text-4xl font-bold text-white mb-4 bg-gradient-to-r from-white to-green-100 bg-clip-text text-transparent">
                                Добро пожаловать в CONCORD
                            </h1>
                            <p className="text-green-100 text-lg max-w-2xl mx-auto">
                                Ваша платформа для создания пассивного дохода через современные инструменты маркетинга и партнерства
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Общий доход */}
                            <div className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-2xl p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-3 rounded-xl bg-emerald-500/20">
                                        <DollarSign className="text-emerald-400" size={28} />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold text-lg">Общий доход</h3>
                                        <p className="text-green-200 text-sm">Все вознаграждения</p>
                                    </div>
                                </div>
                                <div className="text-3xl font-bold text-emerald-400 mb-2">
                                    ${userData?.balance?.toFixed(2) || '0.00'}
                                </div>
                                <div className="text-green-300 text-sm">
                                    За все время участия в проекте
                                </div>
                            </div>

                            {/* Численность команды */}
                            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-3 rounded-xl bg-blue-500/20">
                                        <Users className="text-blue-400" size={28} />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold text-lg">Численность команды</h3>
                                        <p className="text-blue-200 text-sm">Общая структура</p>
                                    </div>
                                </div>
                                <div className="text-3xl font-bold text-blue-400 mb-2">
                                    {partnersStructure.length + (partnersStructure.reduce((sum, p) => sum + (p.children?.length || 0), 0))}
                                </div>
                                <div className="text-blue-300 text-sm">
                                    Активных партнеров: {userData?.active_referrals_count || 0}
                                </div>
                            </div>
                        </div>

                        {/* Дополнительная статистика */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-lg bg-yellow-500/20">
                                        <Crown className="text-yellow-400" size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold">Ваш уровень</h3>
                                        <p className="text-yellow-200 text-sm">Текущий статус</p>
                                    </div>
                                </div>
                                <div className="text-2xl font-bold text-yellow-400">Уровень {userData?.user_level || 1}</div>
                            </div>

                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-lg bg-purple-500/20">
                                        <Calendar className="text-purple-400" size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold">В проекте</h3>
                                        <p className="text-purple-200 text-sm">Дата регистрации</p>
                                    </div>
                                </div>
                                <div className="text-lg font-bold text-purple-400">
                                    {userData?.created_at ? new Date(userData.created_at).toLocaleDateString('ru-RU') : 'Неизвестно'}
                                </div>
                            </div>

                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-lg bg-green-500/20">
                                        <User className="text-green-400" size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold">Профиль</h3>
                                        <p className="text-green-200 text-sm">Личная информация</p>
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-green-400">
                                    {userData?.full_name || userData?.first_name || 'Пользователь'}
                                </div>
                                <div className="text-xs text-green-300">
                                    @{userData?.login || 'username'}
                                </div>
                            </div>
                        </div>

                        {/* Реферальная ссылка */}
                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                            <h3 className="text-xl font-semibold text-white mb-4">Ваша реферальная ссылка</h3>
                            <div className="bg-white/5 rounded-lg p-4 flex items-center justify-between">
                                <code className="text-emerald-400 text-sm">https://b7774c.ru/ref/{userData?.id || '12345'}</code>
                                <button
                                    onClick={copyReferralLink}
                                    className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 p-2 rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <Copy size={16} />
                                    Копировать
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 'finances':
                return (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-white">Финансы</h2>
                            <div className="text-right">
                                <div className="text-sm text-gray-400">Общий доход</div>
                                <div className="text-2xl font-bold text-emerald-400">
                                    ${userData?.balance?.toFixed(2) || '0.00'}
                                </div>
                            </div>
                        </div>

                        {/* Статистика */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <Gift className="text-emerald-400" size={20} />
                                    <span className="text-white text-sm font-medium">Сегодня</span>
                                </div>
                                <div className="text-xl font-bold text-emerald-400">$75.00</div>
                            </div>

                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <Calendar className="text-blue-400" size={20} />
                                    <span className="text-white text-sm font-medium">Этот месяц</span>
                                </div>
                                <div className="text-xl font-bold text-blue-400">$1,250.00</div>
                            </div>

                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <BarChart3 className="text-purple-400" size={20} />
                                    <span className="text-white text-sm font-medium">Операций</span>
                                </div>
                                <div className="text-xl font-bold text-purple-400">{rewardsHistory.length}</div>
                            </div>

                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <TrendingUp className="text-yellow-400" size={20} />
                                    <span className="text-white text-sm font-medium">Средний</span>
                                </div>
                                <div className="text-xl font-bold text-yellow-400">
                                    ${rewardsHistory.length > 0 ?
                                    (rewardsHistory.reduce((sum, r) => sum + r.amount, 0) / rewardsHistory.length).toFixed(2) :
                                    '0.00'}
                                </div>
                            </div>
                        </div>

                        {/* История вознаграждений */}
                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-semibold text-white">История вознаграждений</h3>
                                <button
                                    onClick={exportRewards}
                                    className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <Download size={16} />
                                    Экспорт
                                </button>
                            </div>

                            <div className="space-y-4">
                                {rewardsHistory.map((reward) => (
                                    <div key={reward.id} className="bg-white/5 rounded-xl p-4 border border-white/10">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="p-2 rounded-lg bg-emerald-500/20">
                                                    <Gift className="text-emerald-400" size={20} />
                                                </div>
                                                <div>
                                                    <div className="text-white font-medium">{reward.comment}</div>
                                                    <div className="text-gray-400 text-sm">{reward.created_at}</div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-emerald-400 font-semibold text-lg">
                                                    +${reward.amount.toFixed(2)}
                                                </div>
                                                <div className="text-green-300 text-xs">{reward.status_name || 'Выполнено'}</div>
                                            </div>
                                        </div>
                                        {reward.partner_info && (
                                            <div className="mt-3 pl-12">
                                                <div className="text-sm text-gray-300">
                                                    От партнера: <span className="text-white font-medium">{reward.partner_info.partner_name}</span>
                                                    {reward.partner_info.level && (
                                                        <span className="ml-2 px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs">
                             Уровень {reward.partner_info.level}
                           </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {rewardsHistory.length === 0 && (
                                    <div className="text-center py-8 text-gray-400">
                                        История вознаграждений пока пуста
                                    </div>
                                )}
                            </div>

                            {rewardsHistory.length > 0 && (
                                <div className="mt-6 text-center">
                                    <button className="text-gray-400 hover:text-white transition-colors"></button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 'partners':
                return (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-white">Партнеры</h2>
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={loadDashboardData}
                                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white transition-colors"
                                    title="Обновить данные"
                                >
                                    <RefreshCw size={16} />
                                </button>
                                <div className="text-right">
                                    <div className="text-sm text-gray-400">Общая структура</div>
                                    <div className="text-xl font-bold text-blue-400">
                                        {partnersStructure.length + partnersStructure.reduce((sum, p) => sum + (p.children?.length || 0), 0)} партнеров
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Статистика структуры */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-emerald-400">{partnersStructure.length}</div>
                                    <div className="text-sm text-gray-400">Прямые партнеры</div>
                                </div>
                            </div>

                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-blue-400">
                                        {partnersStructure.reduce((sum, p) => sum + (p.children?.length || 0), 0)}
                                    </div>
                                    <div className="text-sm text-gray-400">Структура</div>
                                </div>
                            </div>

                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-purple-400">
                                        {Math.max(...partnersStructure.map(p => p.level), 0)}
                                    </div>
                                    <div className="text-sm text-gray-400">Макс. уровень</div>
                                </div>
                            </div>

                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-yellow-400">
                                        {partnersStructure.length > 0 ? '100%' : '0%'}
                                    </div>
                                    <div className="text-sm text-gray-400">Активность</div>
                                </div>
                            </div>
                        </div>

                        {/* Рекурсивная структура партнеров */}
                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-semibold text-white">Структура партнеров</h3>
                                <button
                                    className="text-gray-400 hover:text-white transition-colors"
                                    title="Развернуть все"
                                    onClick={() => {
                                        const allIds = {};
                                        const collectIds = (partners) => {
                                            partners.forEach(p => {
                                                allIds[p.id] = true;
                                                if (p.children) collectIds(p.children);
                                            });
                                        };
                                        collectIds(partnersStructure);
                                        setExpandedPartners(allIds);
                                    }}
                                >
                                    <Eye size={20} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {partnersStructure.map((partner) => (
                                    <PartnerNode key={partner.id} partner={partner} />
                                ))}
                            </div>

                            {partnersStructure.length === 0 && (
                                <div className="text-center py-8 text-gray-400">
                                    <Users className="mx-auto mb-4 text-gray-500" size={48} />
                                    <p>У вас пока нет партнеров</p>
                                    <p className="text-sm mt-2">Поделитесь своей реферальной ссылкой!</p>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'structure':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-white mb-6">Структура</h2>

                        <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-2xl p-8 text-center">
                            <div className="mb-6">
                                <Construction className="mx-auto text-orange-400 mb-4" size={64} />
                                <h3 className="text-2xl font-semibold text-white mb-2">В разработке</h3>
                                <p className="text-gray-300 max-w-2xl mx-auto">
                                    Раздел "Структура" находится в активной разработке. Здесь будут размещены
                                    расширенные инструменты анализа и управления вашей партнерской сетью.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="bg-white/5 rounded-xl p-4">
                                    <BarChart3 className="text-blue-400 mx-auto mb-2" size={32} />
                                    <h4 className="text-white font-medium mb-1">Аналитика</h4>
                                    <p className="text-gray-400 text-sm">Детальная статистика по уровням</p>
                                </div>

                                <div className="bg-white/5 rounded-xl p-4">
                                    <TrendingUp className="text-green-400 mx-auto mb-2" size={32} />
                                    <h4 className="text-white font-medium mb-1">Прогнозы</h4>
                                    <p className="text-gray-400 text-sm">Расчет потенциального дохода</p>
                                </div>

                                <div className="bg-white/5 rounded-xl p-4">
                                    <Users className="text-purple-400 mx-auto mb-2" size={32} />
                                    <h4 className="text-white font-medium mb-1">Управление</h4>
                                    <p className="text-gray-400 text-sm">Инструменты для работы с командой</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'marketing':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-white mb-6">Маркетинг</h2>

                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                            <h3 className="text-xl font-semibold text-white mb-4">Схема дохода</h3>
                            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-8 text-center">
                                <div className="text-gray-300 mb-4">Здесь будет размещена картинка со схемой дохода</div>
                                <div className="w-full h-64 bg-white/5 rounded-lg flex items-center justify-center">
                                    <FileText className="text-gray-400" size={48} />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                            <h3 className="text-xl font-semibold text-white mb-4">Структура WEB APP</h3>
                            <p className="text-gray-300 mb-4">Реализованные разделы:</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {menuItems.filter(item => !['structure', 'tools'].includes(item.id)).map((item) => (
                                    <div key={item.id} className="flex items-center gap-2 p-3 bg-white/5 rounded-lg">
                                        <CheckCircle className="text-emerald-400" size={16} />
                                        <span className="text-sm text-white">{item.label}</span>
                                    </div>
                                ))}
                            </div>

                            <p className="text-gray-300 mb-2 mt-4">В разработке:</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {menuItems.filter(item => ['structure', 'tools'].includes(item.id)).map((item) => (
                                    <div key={item.id} className="flex items-center gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                                        <Construction className="text-orange-400" size={16} />
                                        <span className="text-sm text-white">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                            <h3 className="text-xl font-semibold text-white mb-4">Особенности системы</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="text-emerald-400 mt-1" size={16} />
                                        <div>
                                            <div className="text-white font-medium">Прямые переводы</div>
                                            <div className="text-gray-400 text-sm">Средства передаются напрямую между участниками</div>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="text-emerald-400 mt-1" size={16} />
                                        <div>
                                            <div className="text-white font-medium">История вознаграждений</div>
                                            <div className="text-gray-400 text-sm">Полная отчетность по всем операциям</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="text-emerald-400 mt-1" size={16} />
                                        <div>
                                            <div className="text-white font-medium">Рекурсивная структура</div>
                                            <div className="text-gray-400 text-sm">Просмотр партнеров на всех уровнях</div>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="text-emerald-400 mt-1" size={16} />
                                        <div>
                                            <div className="text-white font-medium">Общий доход</div>
                                            <div className="text-gray-400 text-sm">Суммарные показатели без внутренних балансов</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'tools':
                return (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-white">Наши Инструменты</h2>
                            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
                                <Crown className="text-yellow-400" size={16} />
                                <span className="text-white text-sm font-semibold">Уровень {userData?.user_level || 1}</span>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-2xl p-6 mb-8">
                            <div className="flex items-center gap-3 mb-4">
                                <Construction className="text-orange-400" size={24} />
                                <h3 className="text-xl font-semibold text-white">Все инструменты в разработке</h3>
                            </div>
                            <p className="text-gray-300 text-sm">
                                Команда активно работает над созданием уникальных инструментов для автоматизации
                                вашего бизнеса. Следите за обновлениями!
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {tools.map((tool) => (
                                <ToolCard key={tool.id} tool={tool} />
                            ))}
                        </div>

                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                            <h3 className="text-xl font-semibold text-white mb-4">Планируемые возможности</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <h4 className="text-white font-medium">Персонализация</h4>
                                    <ul className="space-y-2 text-sm text-gray-300">
                                        <li>• Индивидуальный дизайн для каждого инструмента</li>
                                        <li>• Настройка под ваш бренд и стиль</li>
                                        <li>• Персональные реферальные ссылки</li>
                                    </ul>
                                </div>

                                <div className="space-y-3">
                                    <h4 className="text-white font-medium">Аналитика</h4>
                                    <ul className="space-y-2 text-sm text-gray-300">
                                        <li>• Отслеживание конверсий</li>
                                        <li>• Статистика использования</li>
                                        <li>• Детальная аналитика эффективности</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'presentations':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-white mb-6">Презентации</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                                <h3 className="text-xl font-semibold text-white mb-4">Слайды PDF</h3>
                                <div className="mb-4">
                                    <div className="w-full h-32 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center border border-blue-500/20">
                                        <FileText className="text-blue-400" size={48} />
                                    </div>
                                </div>
                                <button className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2">
                                    <Download size={20} />
                                    Скачать презентацию
                                </button>
                            </div>

                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                                <h3 className="text-xl font-semibold text-white mb-4">Преимущества</h3>
                                <div className="mb-4">
                                    <div className="w-full h-32 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center border border-purple-500/20">
                                        <Star className="text-purple-400" size={48} />
                                    </div>
                                </div>
                                <div className="text-center text-gray-300 text-sm">
                                    6 основных преимуществ системы CONCORD
                                </div>
                            </div>
                        </div>

                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                            <h3 className="text-xl font-semibold text-white mb-4">Материалы для партнеров</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[
                                    { name: 'Инструкция для новичков', icon: FileText, color: 'emerald' },
                                    { name: 'Схемы вознаграждений', icon: TrendingUp, color: 'blue' },
                                    { name: 'FAQ по системе', icon: MessageCircle, color: 'purple' }
                                ].map((material) => (
                                    <div key={material.name} className={`bg-${material.color}-500/10 border border-${material.color}-500/20 rounded-xl p-4 hover:bg-${material.color}-500/20 transition-all duration-200 cursor-pointer`}>
                                        <material.icon className={`text-${material.color}-400 mb-2`} size={24} />
                                        <h4 className="text-white font-medium text-sm">{material.name}</h4>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 'documents':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-white mb-6">Документы</h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                { name: 'Соглашение', description: 'Пользовательское соглашение', icon: File, color: 'emerald' },
                                { name: 'Этика и Правила', description: 'Правила поведения в сообществе', icon: FileText, color: 'blue' },
                                { name: 'Правила от лидеров', description: 'Рекомендации опытных участников', icon: Star, color: 'purple' }
                            ].map((doc) => (
                                <div key={doc.name} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-200 cursor-pointer">
                                    <div className={`p-3 rounded-xl bg-${doc.color}-500/20 w-fit mb-4`}>
                                        <doc.icon className={`text-${doc.color}-400`} size={24} />
                                    </div>
                                    <h3 className="text-white font-semibold mb-2">{doc.name}</h3>
                                    <p className="text-gray-400 text-sm mb-4">{doc.description}</p>
                                    <div className="flex items-center gap-2 text-gray-300 text-sm hover:text-white transition-colors">
                                        <ExternalLink size={14} />
                                        Открыть документ
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                            <h3 className="text-xl font-semibold text-white mb-4">Важная информация</h3>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="text-emerald-400 mt-1" size={16} />
                                    <div>
                                        <div className="text-white font-medium">Прозрачность операций</div>
                                        <div className="text-gray-400 text-sm">Все транзакции проходят напрямую между участниками</div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <CheckCircle className="text-emerald-400 mt-1" size={16} />
                                    <div>
                                        <div className="text-white font-medium">Соблюдение правил</div>
                                        <div className="text-gray-400 text-sm">Обязательно ознакомьтесь с этикой и правилами</div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <CheckCircle className="text-emerald-400 mt-1" size={16} />
                                    <div>
                                        <div className="text-white font-medium">Поддержка</div>
                                        <div className="text-gray-400 text-sm">При вопросах обращайтесь к администрации</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'groups':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-white mb-6">Группы и каналы</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[
                                { name: 'Главная группа', description: 'Основное сообщество участников', members: '1,234', color: 'emerald' },
                                { name: 'Новости и обновления', description: 'Актуальная информация о проекте', members: '2,105', color: 'blue' },
                                { name: 'Наши уровни', description: 'Обсуждение программ лояльности', members: '567', color: 'purple' },
                                { name: 'Промоматериалы', description: 'Материалы для продвижения', members: '890', color: 'yellow' },
                                { name: 'Техническая поддержка', description: 'Помощь по техническим вопросам', members: '445', color: 'orange' },
                                { name: 'Успешные кейсы', description: 'Истории успеха участников', members: '623', color: 'pink' }
                            ].map((group) => (
                                <div key={group.name} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-200">
                                    <div className={`p-3 rounded-xl bg-${group.color}-500/20 w-fit mb-4`}>
                                        <MessageCircle className={`text-${group.color}-400`} size={24} />
                                    </div>
                                    <h3 className="text-white font-semibold mb-2">{group.name}</h3>
                                    <p className="text-gray-400 text-sm mb-4">{group.description}</p>
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-gray-300 text-sm">{group.members} участников</span>
                                        <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                                    </div>
                                    <button className={`w-full bg-${group.color}-500/20 hover:bg-${group.color}-500/30 text-${group.color}-400 py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2`}>
                                        <ExternalLink size={16} />
                                        Присоединиться
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-6">
                            <h3 className="text-xl font-semibold text-white mb-2">Правила участия в группах</h3>
                            <p className="text-gray-300 text-sm mb-4">
                                Для комфортного общения всех участников просим соблюдать правила:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm text-gray-300">
                                        <CheckCircle className="text-emerald-400" size={14} />
                                        <span>Уважительное общение</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-300">
                                        <CheckCircle className="text-emerald-400" size={14} />
                                        <span>Без спама и рекламы</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm text-gray-300">
                                        <CheckCircle className="text-emerald-400" size={14} />
                                        <span>Конструктивные обсуждения</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-300">
                                        <CheckCircle className="text-emerald-400" size={14} />
                                        <span>Соблюдение этики</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-green-900 relative overflow-hidden">
            {/* Animated background effects */}
            <div className="absolute inset-0">
                <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-20 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
                <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-yellow-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
            </div>

            <div className="relative z-10">
                {/* Header */}
                <header className="bg-white/5 backdrop-blur-md border-b border-white/10">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between h-16">
                            <div className="flex items-center gap-4">
                                {/* Menu button */}
                                <button
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                    className="p-2 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-colors"
                                >
                                    {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
                                </button>

                                {/* CONCORD Logo */}
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center">
                                        <div className="text-white font-bold text-lg">C</div>
                                    </div>
                                    <div>
                                        <h1 className="text-xl font-bold text-white">CONCORD</h1>
                                        <p className="text-xs text-green-300">Бесконечный доход</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
                                    <User className="text-gray-400" size={16} />
                                    <span className="text-white text-sm">{userData?.first_name || 'Пользователь'}</span>
                                </div>
                                <button className="p-2 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-gray-400 hover:text-white transition-colors">
                                    <Settings size={20} />
                                </button>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Sidebar Overlay */}
                {isMenuOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30"
                        onClick={() => setIsMenuOpen(false)}
                    />
                )}

                {/* Sidebar Navigation */}
                <div className={`fixed left-0 top-16 h-[calc(100vh-4rem)] w-80 bg-green-800/80 backdrop-blur-md border-r border-green-600/30 z-40 transform transition-transform duration-300 ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'
                }`}>
                    <div className="p-6">
                        <nav className="space-y-2">
                            {menuItems.map((item) => {
                                const Icon = item.icon;
                                const isInDevelopment = ['structure', 'tools'].includes(item.id);
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => {
                                            setActiveTab(item.id);
                                            setIsMenuOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${activeTab === item.id
                                            ? 'bg-gradient-to-r from-emerald-600/40 to-green-700/40 border border-emerald-500/50 text-white shadow-lg shadow-emerald-500/20'
                                            : `bg-white/10 backdrop-blur-md border border-white/20 text-green-100 hover:bg-white/20 hover:text-white ${isInDevelopment ? 'opacity-80' : ''}`
                                        }`}
                                    >
                                        <Icon size={20} />
                                        <span className="font-medium flex-1">{item.label}</span>
                                        {isInDevelopment && (
                                            <Construction className="text-orange-400" size={16} />
                                        )}
                                    </button>
                                );
                            })}
                        </nav>

                        {/* User info in sidebar */}
                        <div className="mt-8 p-4 bg-white/10 rounded-xl border border-white/20">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                                    <User className="text-white" size={16} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-white font-medium text-sm truncate">
                                        {userData?.full_name || `${userData?.first_name} ${userData?.last_name}` || 'Пользователь'}
                                    </div>
                                    <div className="text-green-300 text-xs">@{userData?.login || 'username'}</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-center">
                                <div className="bg-white/10 rounded-lg p-2">
                                    <div className="text-emerald-400 font-bold text-sm">${userData?.balance?.toFixed(0) || '0'}</div>
                                    <div className="text-gray-400 text-xs">Доход</div>
                                </div>
                                <div className="bg-white/10 rounded-lg p-2">
                                    <div className="text-blue-400 font-bold text-sm">
                                        {partnersStructure.length + partnersStructure.reduce((sum, p) => sum + (p.children?.length || 0), 0)}
                                    </div>
                                    <div className="text-gray-400 text-xs">Команда</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 lg:p-8 min-h-[600px]">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConcordWebApp;
